import { Camera, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/utils/cn';

import { ImageCropper } from './image-cropper';

/**
 * Photo upload with a circular crop.
 *
 * Choosing a file opens the cropper immediately rather than uploading and letting the
 * user fix it afterwards, because a badly framed member photo left in place is one
 * nobody ever goes back to.
 */

export interface AvatarUploaderProps {
  /** Current photo URL, if any. */
  src?: string | null;
  /** Used for the fallback initials and the image's alt text. */
  name: string;
  onUpload: (blob: Blob) => Promise<void> | void;
  onRemove?: () => void;
  disabled?: boolean;
  size?: 'lg' | 'xl';
  labels: {
    change: string;
    remove: string;
    cropTitle: string;
    zoom: string;
    apply: string;
    cancel: string;
    instructions: string;
    closeCropper?: string;
  };
  className?: string;
}

export const AvatarUploader = ({
  src,
  name,
  onUpload,
  onRemove,
  disabled = false,
  size = 'xl',
  labels,
  className,
}: AvatarUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [candidate, setCandidate] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Object URLs are revoked when they are replaced or the component goes away; a
  // form opened and closed a dozen times otherwise holds every image in memory.
  useEffect(
    () => () => {
      if (candidate) URL.revokeObjectURL(candidate);
    },
    [candidate],
  );

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <Avatar src={src} name={name} size={size} />

      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          leadingIcon={<Camera className="h-4 w-4" />}
        >
          {labels.change}
        </Button>

        {src && onRemove ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onRemove}
            leadingIcon={<Trash2 className="h-4 w-4" />}
            className="text-danger-fg"
          >
            {labels.remove}
          </Button>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) setCandidate(URL.createObjectURL(file));
            event.target.value = '';
          }}
        />
      </div>

      <Dialog open={candidate !== null} onOpenChange={(open) => !open && setCandidate(null)}>
        <DialogContent size="md" closeLabel={labels.closeCropper}>
          <DialogHeader>
            <DialogTitle>{labels.cropTitle}</DialogTitle>
          </DialogHeader>

          {candidate ? (
            <ImageCropper
              src={candidate}
              aspect={1}
              circular
              maxSize={512}
              applying={uploading}
              onCancel={() => setCandidate(null)}
              onCropped={async (blob) => {
                setUploading(true);
                try {
                  await onUpload(blob);
                  setCandidate(null);
                } finally {
                  setUploading(false);
                }
              }}
              labels={{
                zoom: labels.zoom,
                apply: labels.apply,
                cancel: labels.cancel,
                instructions: labels.instructions,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
